import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  LinearProgress,
  Stack,
  Grid2 as Grid,
  Paper,
  Card,
  CardMedia,
  Alert,
  Slide,
  CircularProgress,
  Chip,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { useDropzone } from "react-dropzone";
import { HugeiconsIcon } from "@hugeicons/react";
import { DataGrid } from "@/components";
import ContentDrawer from "@/pages/organization-content/Drawer";
import {
  Cancel01Icon,
  ImageUploadIcon,
  Image01Icon,
  Delete02Icon,
  SparklesIcon,
  Tick01Icon,
  ArrowRight01Icon,
  Edit02Icon,
} from "@hugeicons/core-free-icons";
import { storage } from "@/firebase";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, UploadTask } from "firebase/storage";
import { useTerminology } from "@/hooks";
import { useAnalyzeCatalogMutation, useCleanupTempCatalogImagesMutation } from "@/features/catalog/api/aiCatalogApi";
import { useAddContentMutation } from "@/features/organizations/api/contentApi";
import { useFetchMenuByIdQuery, useUpdateMenuMutation } from "@/features/organizations/api/menusApi";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  catalogId: string;
}

type Step = "upload" | "analyzing" | "review" | "importing" | "success";

interface TempFile {
  file: File;
  preview: string;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  uploadedPath?: string;
  downloadUrl?: string;
}

export const CatalogImportWizard = ({ open, onClose, organizationId, catalogId }: Props) => {
  const { t, product, catalog } = useTerminology();
  const [step, setStep] = useState<Step>("upload");
  const [files, setFiles] = useState<TempFile[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const activeUploadTasks = useRef<Record<string, UploadTask>>({});

  // Local state for extracted products and sections
  const [products, setProducts] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [editingProduct, setEditingProduct] = useState<any | null>(null);

  const [analyzeCatalog] = useAnalyzeCatalogMutation();
  const [cleanupTempImages] = useCleanupTempCatalogImagesMutation();
  const [addContent] = useAddContentMutation();
  const [updateMenu] = useUpdateMenuMutation();
  const menuQuery = useFetchMenuByIdQuery({ organizationId, menuId: catalogId });

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach((f) => URL.revokeObjectURL(f.preview));
    };
  }, [files]);

  // Dropzone implementation
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      setErrorMsg(null);

      if (files.length + acceptedFiles.length > 10) {
        setErrorMsg("Límite diario excedido. Puedes subir un máximo de 10 imágenes.");
        return;
      }

      if (rejectedFiles.length > 0) {
        const hasLargeFile = rejectedFiles.some((rf) => rf.file.size > 5 * 1024 * 1024);
        if (hasLargeFile) {
          setErrorMsg("Una o más imágenes superan el tamaño máximo permitido de 5 MB.");
        } else {
          setErrorMsg("Por favor, sube archivos de imagen válidos (PNG, JPG, JPEG).");
        }
      }

      const newFiles = acceptedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        progress: 0,
        status: "pending" as const,
      }));

      setFiles((prev) => [...prev, ...newFiles]);
    },
    [files]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const removeFileBeforeUpload = (index: number) => {
    setFiles((prev) => {
      const target = prev[index];
      if (target) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // Cumulative progress calculation
  const cumulativeProgress = useMemo(() => {
    if (files.length === 0) return 0;
    const totalProgress = files.reduce((sum, f) => sum + f.progress, 0);
    return Math.round(totalProgress / files.length);
  }, [files]);

  // Cleanup temporary storage images helper
  const handleCleanupTempStorage = async (filesToClean: TempFile[]) => {
    const deletePromises = filesToClean
      .filter((f) => f.status === "success" && f.uploadedPath)
      .map(async (f) => {
        try {
          const fileRef = ref(storage, f.uploadedPath!);
          await deleteObject(fileRef);
        } catch (e) {
          console.error("Error deleting temp storage file:", e);
        }
      });
    await Promise.all(deletePromises);
  };

  // Local edit/delete helpers
  const handleStartEdit = (row: any) => {
    setEditingProduct(row);
  };

  const handleSaveEdit = (updatedContent: any) => {
    if (!editingProduct) return;

    // Destructure only the relevant fields to avoid overriding other local properties
    const { title, description, price } = updatedContent;

    // 1. Update product in local state
    setProducts((prev) =>
      prev.map((p) =>
        p.id === editingProduct.id
          ? {
              ...p,
              title: title !== undefined ? title : p.title,
              description: description !== undefined ? description : p.description,
              price: price !== undefined ? price : p.price,
            }
          : p
      )
    );

    setEditingProduct(null);
  };

  const handleDeleteProduct = (productId: string) => {
    const confirmDelete = window.confirm(`¿Estás seguro de que deseas eliminar este ${product}?`);
    if (!confirmDelete) return;

    // 1. Remove product from products list
    setProducts((prev) => prev.filter((p) => p.id !== productId));

    // 2. Remove product ID from all sections
    setSections((prev) =>
      prev
        .map((sec) => ({
          ...sec,
          products: sec.products.filter((pid) => pid !== productId),
        }))
        .filter((sec) => sec.products.length > 0)
    );
  };

  // Grid columns and rows computation
  const gridRows = useMemo(() => {
    return products.map((prod) => {
      const section = sections.find((sec) => sec.products.includes(prod.id));
      return {
        ...prod,
        category: section ? section.category : "",
      };
    });
  }, [products, sections]);

  const columns = useMemo(
    () => [
      { field: "title", headerName: "Nombre", flex: 1.2, minWidth: 150 },
      { field: "description", headerName: "Descripción", flex: 2, minWidth: 250 },
      {
        field: "price",
        headerName: "Precio",
        flex: 0.8,
        minWidth: 100,
        renderCell: (params: any) => {
          const val = params.value;
          return val !== null && val !== undefined ? `$${val}` : "—";
        },
      },
      {
        field: "category",
        headerName: "Categoría",
        flex: 1,
        minWidth: 150,
        renderCell: (params: any) => (
          <Chip label={params.value || "Sin Categoría"} size="small" variant="outlined" color="primary" />
        ),
      },
      {
        field: "actions",
        headerName: "Acciones",
        flex: 0.8,
        minWidth: 120,
        sortable: false,
        renderCell: (params: any) => (
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
            <IconButton
              size="small"
              onClick={() => handleStartEdit(params.row)}
              color="primary"
              id={`edit-prod-${params.row.id}`}
            >
              <HugeiconsIcon icon={Edit02Icon} size={24} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteProduct(params.row.id)}
              color="error"
              id={`delete-prod-${params.row.id}`}
            >
              <HugeiconsIcon icon={Delete02Icon} size={24} />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [product, sections]
  );

  // Save flow to write products and update catalog in Firestore
  const handleStartImport = async () => {
    if (!menuQuery.data) {
      setErrorMsg("No se pudieron cargar los datos del catálogo. Inténtalo de nuevo.");
      return;
    }

    setStep("importing");
    setErrorMsg(null);

    try {
      const tempToRealIdMap: Record<string, string> = {};

      // 1. Create all products in Firestore
      for (const prod of products) {
        const draftContent = {
          title: prod.title,
          description: prod.description || "",
          previewDescription: prod.previewDescription || "",
          price: prod.price || 0,
          images: prod.images || [],
          status: "published" as const,
        };

        const result = await addContent({
          draft: draftContent,
          organizationId,
        }).unwrap();

        if (prod.id && result.id) {
          tempToRealIdMap[prod.id] = result.id;
        }
      }

      // 2. Map sections products to their new real Firestore IDs
      const updatedStructure = sections.map((sec) => {
        const mappedProducts = (sec.products || [])
          .map((tempId: string) => tempToRealIdMap[tempId])
          .filter(Boolean);

        return {
          category: sec.category,
          categoryTitle: sec.category,
          isVisible: true,
          columns: 1,
          template: "default-filled" as const,
          products: mappedProducts,
        };
      });

      // 3. Update the menu in Firestore with the new structure
      const currentStructure = menuQuery.data.structure || [];
      const finalStructure = [...currentStructure, ...updatedStructure];

      await updateMenu({
        menu: {
          ...menuQuery.data,
          structure: finalStructure,
        },
        organizationId,
      }).unwrap();

      // 4. Trigger backend cleanup for Storage temporary files
      try {
        await cleanupTempImages({ organizationId, catalogId }).unwrap();
      } catch (e) {
        console.error("Storage cleanup failed during final save:", e);
      }

      // 5. Complete flow
      setStep("success");

      setTimeout(() => {
        setFiles([]);
        setProducts([]);
        setSections([]);
        setStep("upload");
        onClose();
      }, 2000);

    } catch (err: any) {
      console.error("Import failed:", err);
      setErrorMsg(
        err?.data?.statusText ||
        err?.statusText ||
        err?.message ||
        "Hubo un error al crear los productos en la base de datos. Inténtalo de nuevo."
      );
      setStep("review");
    }
  };

  // Cancel flow with browser confirmation
  const handleCancel = async () => {
    const confirmCancel = window.confirm(
      "¿Estás seguro de que deseas cancelar el proceso? Se perderán todas las imágenes cargadas."
    );
    if (!confirmCancel) return;

    // 1. Cancel active upload tasks
    Object.values(activeUploadTasks.current).forEach((task) => {
      try {
        task.cancel();
      } catch (e) {
        console.error("Error canceling upload task:", e);
      }
    });
    activeUploadTasks.current = {};

    // 2. Trigger backend cleanup for the temp folder
    try {
      await cleanupTempImages({ organizationId, catalogId }).unwrap();
    } catch (e) {
      console.error("Error calling cleanupTempCatalogImages backend:", e);
      // Fallback local cleanup
      const uploadedFiles = files.filter((f) => f.status === "success");
      if (uploadedFiles.length > 0) {
        await handleCleanupTempStorage(uploadedFiles);
      }
    }

    // 3. Reset and close
    setFiles([]);
    setProducts([]);
    setSections([]);
    setStep("upload");
    onClose();
  };

  const triggerAnalysis = async (uploadedFiles: TempFile[]) => {
    setStep("analyzing");
    try {
      const filePaths = uploadedFiles
        .map((f) => f.uploadedPath)
        .filter(Boolean) as string[];

      const response = await analyzeCatalog({
        organizationId,
        catalogId,
        filePaths,
      }).unwrap();

      setProducts(response.products || []);
      setSections(response.sections || []);
      setStep("review");
    } catch (err: any) {
      console.error("Error during analyzeCatalog call:", err);
      setErrorMsg(
        err?.data?.statusText ||
        err?.statusText ||
        err?.message ||
        "Error al analizar las imágenes. Por favor, verifica tu API Key."
      );
      setStep("upload");
    }
  };

  // Start upload of pending images
  const handleStartUpload = async () => {
    if (files.length === 0) {
      setErrorMsg("Por favor, selecciona al menos una foto de tu catálogo físico.");
      return;
    }

    setErrorMsg(null);
    const uploadPromises = files.map((tempFile, idx) => {
      if (tempFile.status === "success") return Promise.resolve();

      return new Promise<void>((resolve) => {
        const fileExtension = tempFile.file.name.split(".").pop();
        const tempPath = `temp/${organizationId}/${catalogId}/${Date.now()}_${idx}.${fileExtension}`;
        const storageRef = ref(storage, tempPath);
        const uploadTask = uploadBytesResumable(storageRef, tempFile.file);

        activeUploadTasks.current[tempPath] = uploadTask;

        setFiles((prev) => {
          const updated = [...prev];
          if (updated[idx]) {
            updated[idx].status = "uploading";
            updated[idx].uploadedPath = tempPath;
          }
          return updated;
        });

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setFiles((prev) => {
              const updated = [...prev];
              if (updated[idx]) {
                updated[idx].progress = progress;
              }
              return updated;
            });
          },
          (error) => {
            console.error("Upload error for file index", idx, error);
            setFiles((prev) => {
              const updated = [...prev];
              if (updated[idx]) {
                updated[idx].status = "error";
                updated[idx].progress = 0;
              }
              return updated;
            });
            delete activeUploadTasks.current[tempPath];
            resolve(); // Resolve to let other uploads complete
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            setFiles((prev) => {
              const updated = [...prev];
              if (updated[idx]) {
                updated[idx].status = "success";
                updated[idx].progress = 100;
                updated[idx].downloadUrl = url;
              }
              return updated;
            });
            delete activeUploadTasks.current[tempPath];
            resolve();
          }
        );
      });
    });

    await Promise.all(uploadPromises);

    // Verify all uploads completed successfully
    setFiles((currentFiles) => {
      const anyError = currentFiles.some((f) => f.status === "error");
      const allDone = currentFiles.every((f) => f.status === "success");

      if (anyError) {
        setErrorMsg("Hubo un problema al subir algunas imágenes. Inténtalo de nuevo.");
      } else if (allDone) {
        triggerAnalysis(currentFiles);
      }
      return currentFiles;
    });
  };

  const isUploading = files.some((f) => f.status === "uploading");

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleCancel}
      TransitionComponent={Transition}
      id="catalog-import-wizard-dialog"
    >
      {/* Top Header */}
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleCancel}
            aria-label="close"
            id="wizard-close-btn"
          >
            <HugeiconsIcon icon={Cancel01Icon} size={24} />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h4" color="white">
            Digitalizar {t("catalog", { capitalize: true })} con IA
          </Typography>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
            id="wizard-cancel-header-btn"
          >
            Cancelar
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Flow Content */}
      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 2, md: 4 }, bgcolor: "background.default" }}>
        {step === "upload" && (
          <Stack spacing={3} sx={{ maxWidth: 900, mx: "auto" }}>
            <Typography variant="h2" align="center" fontWeight="bold">
              Sube fotos de tu {catalog} físico
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary">
              Toma fotos claras de las páginas de tu {catalog}. La IA identificará los nombres de los{" "}
              {t("product", 2)}, sus descripciones, precios y secciones correspondientes de forma automática.
            </Typography>

            {errorMsg && (
              <Alert severity="error" onClose={() => setErrorMsg(null)} id="wizard-error-alert">
                {errorMsg}
              </Alert>
            )}

            {/* Dropzone Container */}
            {!isUploading && (
              <Paper
                {...getRootProps()}
                variant="outlined"
                sx={{
                  p: 6,
                  textAlign: "center",
                  border: "2px dashed",
                  borderColor: isDragActive ? "primary.main" : "divider",
                  bgcolor: isDragActive ? "action.hover" : "background.paper",
                  cursor: "pointer",
                  borderRadius: 3,
                  transition: "border-color 0.2s, background-color 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  },
                }}
                id="wizard-dropzone"
              >
                <input {...getInputProps()} />
                <Stack spacing={2} alignItems="center">
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "50%",
                      bgcolor: "primary.light",
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <HugeiconsIcon icon={ImageUploadIcon} size={40} />
                  </Box>
                  <Typography variant="h4" fontWeight="bold">
                    {isDragActive ? "Suelta las fotos aquí" : "Arrastra tus fotos o haz clic para buscar"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Formatos admitidos: PNG, JPG, JPEG. Tamaño máximo: 5 MB por foto. Límite: 10 fotos.
                  </Typography>
                </Stack>
              </Paper>
            )}

            {/* Uploading Progress Bar */}
            {isUploading && (
              <Paper sx={{ p: 4, borderRadius: 3 }} variant="outlined">
                <Stack spacing={2.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4" fontWeight="bold">
                      Subiendo fotos al almacenamiento temporal...
                    </Typography>
                    <Typography variant="h4" color="primary.main" fontWeight="bold">
                      {cumulativeProgress}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={cumulativeProgress}
                    sx={{ height: 10, borderRadius: 5 }}
                    id="wizard-upload-progress-bar"
                  />
                  <Typography variant="body2" color="text.secondary" align="center">
                    Por favor, no cierres esta ventana ni recargues la página.
                  </Typography>
                </Stack>
              </Paper>
            )}

            {/* List of files selected */}
            {files.length > 0 && (
              <Stack spacing={2}>
                <Typography variant="h4" fontWeight="bold">
                  Fotos seleccionadas ({files.length}/10)
                </Typography>
                <Grid container spacing={2}>
                  {files.map((tempFile, idx) => (
                    <Grid key={tempFile.preview} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card variant="outlined" sx={{ position: "relative", borderRadius: 2 }}>
                        <CardMedia
                          component="img"
                          height="140"
                          image={tempFile.preview}
                          alt={`menu_page_${idx}`}
                        />
                        <Box sx={{ p: 1.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ overflow: "hidden" }}>
                              <HugeiconsIcon icon={Image01Icon} size={18} />
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{ textOverflow: "ellipsis", width: "130px" }}
                              >
                                {tempFile.file.name}
                              </Typography>
                            </Stack>

                            {/* Status indicator / delete button */}
                            {tempFile.status === "pending" && !isUploading && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeFileBeforeUpload(idx)}
                                id={`remove-file-${idx}`}
                              >
                                <HugeiconsIcon icon={Delete02Icon} size={16} />
                              </IconButton>
                            )}

                            {tempFile.status === "uploading" && (
                              <CircularProgress size={16} value={tempFile.progress} variant="determinate" />
                            )}

                            {tempFile.status === "success" && (
                              <Box sx={{ color: "success.main", display: "flex" }}>
                                <HugeiconsIcon icon={Tick01Icon} size={18} />
                              </Box>
                            )}

                            {tempFile.status === "error" && (
                              <Typography variant="caption" color="error.main">
                                Error
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            )}

            {/* Actions Bar */}
            {!isUploading && files.length > 0 && (
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  size="large"
                  id="wizard-cancel-footer-btn"
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleStartUpload}
                  endIcon={<HugeiconsIcon icon={ArrowRight01Icon} size={18} />}
                  size="large"
                  id="wizard-start-upload-btn"
                >
                  Subir y Continuar
                </Button>
              </Stack>
            )}
          </Stack>
        )}

        {/* STEP 2: AI Analyzing Spinner */}
        {step === "analyzing" && (
          <Stack
            spacing={4}
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: "60vh", maxWidth: 600, mx: "auto" }}
          >
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress size={80} thickness={4} />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.main",
                }}
              >
                <HugeiconsIcon icon={SparklesIcon} size={32} />
              </Box>
            </Box>
            <Stack spacing={1.5} alignItems="center">
              <Typography variant="h2" fontWeight="bold" align="center">
                Analizando las imágenes...
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                Gemini está extrayendo los {t("product", 2)}, categorías y precios de tus fotos. Esto puede tomar
                unos segundos.
              </Typography>
            </Stack>
          </Stack>
        )}

        {/* STEP 3: Review View showing DataGrid and Actions as requested */}
        {step === "review" && (
          <Stack spacing={3} sx={{ maxWidth: 1200, mx: "auto" }}>
            <Typography variant="h2" fontWeight="bold">
              Revisar e Importar {t("product", { count: 2, capitalize: true })}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              A continuación se presentan los {t("product", 2)} y secciones detectados por Gemini. Puedes editar
              los campos, corregir precios o eliminar filas directamente antes de incorporarlos a tu {catalog}.
            </Typography>

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", bgcolor: "background.paper" }}>
              <DataGrid
                rows={gridRows}
                columns={columns}
                autoHeight
                hideFooter={gridRows.length <= 10}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: "action.hover",
                    borderBottom: 1,
                    borderColor: "divider",
                  },
                }}
              />
            </Paper>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                size="large"
                id="wizard-review-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartImport}
                size="large"
                id="wizard-review-start-import-btn"
              >
                Comenzar Importación
              </Button>
            </Stack>
          </Stack>
        )}

        {/* STEP 4: Creating Products Loader */}
        {step === "importing" && (
          <Stack
            spacing={4}
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: "60vh", maxWidth: 600, mx: "auto" }}
          >
            <CircularProgress size={60} />
            <Stack spacing={1} alignItems="center">
              <Typography variant="h2" fontWeight="bold">
                Creando {t("product", 2)}...
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                Guardando la configuración en base de datos.
              </Typography>
            </Stack>
          </Stack>
        )}

        {/* STEP 5: Success State */}
        {step === "success" && (
          <Stack
            spacing={3}
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: "60vh", maxWidth: 600, mx: "auto" }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "success.light",
                color: "success.contrastText",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: 3,
                animation: "scaleIn 0.3s ease-out",
              }}
            >
              <HugeiconsIcon icon={Tick01Icon} size={48} />
            </Box>
            <Stack spacing={1} alignItems="center">
              <Typography variant="h2" fontWeight="bold" color="success.main">
                ¡Importación Exitosa!
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                Se han creado {products.length} {t("product", { count: products.length })} y {sections.length} secciones en tu {catalog}.
              </Typography>
            </Stack>
          </Stack>
        )}

        {/* Edit Drawer (Product Details Local Editor using ContentDrawer) */}
        <ContentDrawer
          open={!!editingProduct}
          selectedContent={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={handleSaveEdit}
          onDelete={handleDeleteProduct}
          sx={{ zIndex: 1400 }}
        />
      </Box>
    </Dialog>
  );
};

export default CatalogImportWizard;
